/**
 * Model type constants for all objects in the Benchling data model.
 */

import _ from 'underscore';

import {NucleotideType} from 'biojs/BioConstants';
/*
 * USAGE:
 * - Newer models should use DomainGraphType as the source of truth for type identity
 * - This file is maintained for backwards compatibility
 * - New types should still be added here for compatibility
 *
 * NOTES:
 * - Used by search code generation to identify constants for listing renderers
 * - If updating the format, modify get_search_model_type_constants_for_types in
 *   scripts/search/code_generation/client_helpers.py
 *
 * IMPORTANT:
 * - Do NOT add comments in the body of ModelTypeConstants (will be overridden by codemod tools)
 * - Do NOT use EXAMPLE_TYPE, for Object Framework team use only
 */
enum ModelTypeConstants {
  _DO_NOT_USE_INTERNAL_PIPELINE = 'internal_pipeline',
  AA_MONOMER = 'aa_monomer',
  AA_PART_LINK_FIELD_DEFINITION = 'aa_part_link_field_definition',
  AA_SEQUENCE = 'aa_sequence',
  AA_SEQUENCE_LINK_FIELD_DEFINITION = 'aa_sequence_link_field_definition',
  AA_SEQUENCE_SCHEMA = 'aa_sequence_schema',
  AGENT_SKILL = 'agent_skill',
  AGENT_SKILL_FILE = 'agent_skill_file',
  AI_MODEL = 'ai_model',
  ANALYSIS = 'analysis',
  ANALYSIS_TEMPLATE = 'pipeline_template',
  ANALYSIS_TEMPLATE_DATA_FRAME_VARIABLE = 'analysis_template_data_frame_variable',
  ANALYSIS_TEMPLATE_VARIABLE = 'analysis_template_variable',
  ANALYTICS_DASHBOARD = 'analytics_dashboard',
  ANALYTICS_QUERY = 'analytics_query',
  ANNOTATION = 'annotation',
  ANTIBODY = 'antibody',
  ANTIBODY_FORMAT = 'antibody_format',
  ANTIBODY_LINK_FIELD_DEFINITION = 'antibody_link_field_definition',
  ANTIBODY_SCHEMA = 'antibody_schema',
  ANY_ENTITY_LINK_FIELD_DEFINITION = 'any_entity_link_field_definition',
  APP_CANVAS = 'app_canvas',
  APP_DEFINITION = 'app_definition',
  APP_DEFINITION_VERSION = 'app_definition_version',
  APP_INSTALLATION = 'app_installation',
  APP_SESSION = 'app_session',
  ASSAY_PROCEDURE = 'assay_procedure',
  ASSAY_PROCESS = 'assay_process',
  ASSAY_PROCESS_OUTPUT = 'assay_process_output',
  ASSAY_REQUEST_LINK_FIELD_DEFINITION = 'assay_request_link_field_definition',
  ASSAY_RESULT = 'assay_result',
  ASSAY_RESULT_LINK_FIELD_DEFINITION = 'assay_result_link_field_definition',
  ASSAY_RUN = 'assay_run',
  ASSAY_RUN_LINK_FIELD_DEFINITION = 'assay_run_link_field_definition',
  ASSAY_RUN_TASK = 'assay_run_task',
  ASSAY_SCHEMA = 'assay_schema',
  ASSAY_STEP_TASK = 'assay_step_task',
  ASSAY_UNIT_OPERATION_TASK = 'assay_unit_operation_task',
  ASSEMBLY = 'assembly',
  ASYNC_TASK = 'async_task',
  AUDIT_LOG = 'audit_log',
  AUTOMATION_INPUT_TRANSFORM_FILE = 'automation_input_transform_file',
  AUTOMATION_OUTPUT_FILE = 'automation_output_file',
  AUTOMATION_OUTPUT_TRANSFORM_FILE = 'automation_output_transform_file',
  AV_SCAN_RECORD = 'av_scan_record',
  BASE_FIELD_DEFINITION = 'base_field_definition',
  BASE_MOLECULE_LINK_FIELD_DEFINITION = 'base_molecule_link_field_definition',
  BASIC_FOLDER_ITEM = 'basic_folder_item',
  BATCH = 'batch',
  BATCH_SCHEMA = 'batch_schema',
  BENCHLING_APP = 'benchling_app',
  BENCHLING_OBJECT_SCHEMA_V2 = 'benchling_object_schema',
  BIOENTITY_MIXIN = 'BIOENTITY_MIXIN',
  BLOB = 'result_blob',
  BLOB_LINK_FIELD_DEFINITION = 'blob_link_field_definition',
  BLOCK_VISUALIZATION = 'block_visualization',
  BOOLEAN_FIELD_DEFINITION = 'boolean_field_definition',
  BOX = 'box',
  BOX_SCHEMA = 'box_schema',
  BULK_ASSEMBLY = 'bulk_assembly',
  BUSINESS_RULE = 'business_rule',
  CHART = 'chart',
  CHAT_SESSION = 'chat_session',
  COLLABORATION = 'collaboration',
  CONFIG_IMPORT_HISTORY_ENTRY = 'config_import_history_entry',
  CONNECTION = 'connection',
  CONNECTION_SCHEMA = 'connection_schema',
  CONTAINER = 'container',
  CONTAINER_SCHEMA = 'container_schema',
  CUSTOM_ENTITY = 'custom_entity',
  CUSTOM_ENTITY_LINK_FIELD_DEFINITION = 'custom_entity_link_field_definition',
  CUSTOM_ENTITY_SCHEMA = 'custom_entity_schema',
  DATA_FRAME = 'data_frame',
  DATA_TABLE = 'data_table',
  DATA_TABLE_CONFIG = 'data_table_config',
  DATASET = 'dataset',
  DATE_FIELD_DEFINITION = 'date_field_definition',
  DATE_TIME_FIELD_DEFINITION = 'date_time_field_definition',
  DNA_OLIGO = 'oligo',
  DNA_OLIGO_LINK_FIELD_DEFINITION = 'dna_oligo_link_field_definition',
  DNA_OLIGO_SCHEMA = 'dna_oligo_schema',
  DNA_PART_LINK_FIELD_DEFINITION = 'dna_part_link_field_definition',
  DNA_SEQUENCE_LINK_FIELD_DEFINITION = 'dna_sequence_link_field_definition',
  DNA_SEQUENCE_SCHEMA = 'dna_sequence_schema',
  DOCUMENT = 'document',
  DOCUMENT_VERSION = 'document_version',
  DRAFT_PROCEDURE_RUN = 'draft_procedure_run',
  DROPDOWN = 'dropdown',
  DROPDOWN_FIELD_DEFINITION = 'dropdown_field_definition',
  DROPDOWN_LINK_FIELD_DEFINITION = 'dropdown_link_field_definition',
  ENTITY = 'entity',
  ENTITY_SCHEMA = 'entity_schema',
  ENTRY = 'entry',
  ENTRY_LINK_FIELD_DEFINITION = 'entry_link_field_definition',
  ENTRY_PROTOCOL = 'entry_protocol',
  ENTRY_SCHEMA = 'entry_schema',
  ENZYME_LIST = 'enzyme_list',
  EQUIPMENT = 'equipment',
  EQUIPMENT_LINK_FIELD_DEFINITION = 'equipment_link_field_definition',
  EQUIPMENT_SCHEMA = 'equipment_schema',
  EXAMPLE_TYPE = 'example_type',
  EXTERNAL_FILE = 'external_file',
  FEATURE_LIBRARY = 'feature_library',
  FIELDSET = 'fieldset',
  FIELDSET_LINK_FIELD_DEFINITION = 'fieldset_link_field_definition',
  FILE = 'pipeline_file',
  FIXED_PLATE = 'fixed_plate',
  FLOAT_FIELD_DEFINITION = 'float_field_definition',
  FOLDER = 'folder',
  FORM_DEFINITION = 'form_definition',
  GERMLINE = 'germline',
  GRID = 'grid',
  INGREDIENT = 'ingredient',
  INSTITUTION = 'institution',
  INTEGER_FIELD_DEFINITION = 'integer_field_definition',
  JELLYFISH = 'jellyfish',
  JELLYFISH_SCHEMA = 'jellyfish_schema',
  JSON_FIELD_DEFINITION = 'json_field_definition',
  LABEL_TEMPLATE = 'label_template',
  LADDER = 'ladder',
  LEGACY_APP_INSTALLATION = 'legacy_app_installation',
  LEGACY_DATA_IMPORT_RUN = 'ldi_run',
  LIBRARY_PARAMETER_DEFINITION = 'library_parameter_definition',
  LOCATION = 'location',
  LOCATION_SCHEMA = 'location_schema',
  LONG_TEXT_FIELD_DEFINITION = 'long_text_field_definition',
  LOOKUP_TABLE = 'lookup_table',
  MATRIX_PLATE = 'matrix_plate',
  MATRIX_PLATE_SCHEMA = 'matrix_plate_schema',
  MCP_SERVER_CONFIG = 'mcp_server_config',
  MCP_SERVER_DEFINITION = 'mcp_server_definition',
  MCP_SERVER_USER_CONFIG = 'mcp_server_user_config',
  MIXTURE = 'mixture',
  MIXTURE_INSTRUCTIONS = 'mixture_instructions',
  MIXTURE_LINK_FIELD_DEFINITION = 'mixture_link_field_definition',
  MIXTURE_SCHEMA = 'mixture_schema',
  MOLECULE = 'molecule',
  MOLECULE_LINK_FIELD_DEFINITION = 'molecule_link_field_definition',
  MOLECULE_SCHEMA = 'molecule_schema',
  MONOMER = 'monomer',
  NOTE_FILL_IN_TEXT_BOX = 'note_fill_in_text_box',
  NOTE_LINKED_APP_CANVAS = 'note_linked_app_canvas',
  NOTE_LINKED_ASSAY_DATA = 'note_linked_assay_data',
  NOTE_LINKED_CHART = 'note_linked_chart',
  NOTE_LINKED_DATA = 'note_linked_data',
  NOTE_LINKED_EMBEDDING = 'note_linked_embedding',
  NOTE_LINKED_FILE = 'note_linked_file',
  NOTE_LINKED_GROUP = 'note_linked_group',
  NOTE_LINKED_OBJECT = 'note_linked_object',
  NOTE_LINKED_PLATE_DESIGN = 'note_linked_plate_design',
  NOTE_LINKED_PLATE_RECORD = 'note_linked_plate_transfer_record',
  NOTE_LINKED_SCRATCH_PLATE_DESIGN = 'note_linked_scratch_plate_design',
  NUCLEOTIDE_ALIGNMENT = 'nucleotide_alignment',
  OLIGO_CONJUGATE = 'oligo_conjugate',
  OLIGO_CONJUGATE_LINK_FIELD_DEFINITION = 'oligo_conjugate_link_field_definition',
  OLIGO_CONJUGATE_SCHEMA = 'oligo_conjugate_schema',
  OLIGO_DUPLEX = 'oligo_duplex',
  OLIGO_DUPLEX_LINK_FIELD_DEFINITION = 'oligo_duplex_link_field_definition',
  OLIGO_DUPLEX_SCHEMA = 'oligo_duplex_schema',
  ORGANIZATION = 'organization',
  PIPELINE_STEP = 'pipeline_step',
  PLATE = 'plate',
  PLATE_DESIGN_TEMPLATE = 'plate_design_template',
  PLATE_SCHEMA = 'plate_schema',
  POLICY = 'policy',
  PREDICTION_BATCH = 'model_run',
  PREDICTION_JOB = 'prediction_job',
  PRIMER_PAIR = 'primer_pair',
  PRINTER = 'printer',
  PROCEDURE = 'procedure',
  PROCEDURE_FLOWCHART = 'procedure_flowchart',
  PROCEDURE_FLOWCHART_CONFIG_VERSION = 'procedure_flowchart_config_version',
  PROCEDURE_FLOWCHART_CONFIG_VERSION_PROXY = 'procedure_flowchart_config_version_proxy',
  PROCEDURE_FLOWCHART_VERSION = 'procedure_flowchart_version',
  PROCEDURE_METHOD_DEFINITION_VERSION = 'procedure_method_definition_version',
  PROCEDURE_METHOD_EXECUTION_INSTANCE = 'procedure_method_execution_instance',
  PROCEDURE_METHOD_EXECUTION_INSTANCE_ASSOCIATION = 'procedure_method_execution_instance_association',
  PROCEDURE_PARAMETER = 'procedure_parameter',
  PROCEDURE_PARAMETER_CONFIRMATION_VALUE = 'procedure_parameter_confirmation_value',
  PROCEDURE_PARAMETER_MEASURED_VALUE = 'procedure_parameter_measured_value',
  PROCEDURE_PARAMETER_PLANNED_VALUE = 'procedure_parameter_planned_value',
  PROCEDURE_PARAMETER_VERSION = 'procedure_parameter_version',
  PROCEDURE_RUN = 'procedure_run',
  PROCEDURE_RUN_CONDITION = 'procedure_run_condition',
  PROCEDURE_RUN_CONDITION_REPLICATE = 'procedure_run_condition_replicate',
  PROCEDURE_RUN_PLAN = 'procedure_run_plan',
  PROCEDURE_RUN_PLAN_CONDITION = 'procedure_run_plan_condition',
  PROCEDURE_STEP_DEFINITION_VERSION = 'procedure_step_definition_version',
  PROCEDURE_STEP_EXECUTION_INSTANCE = 'procedure_step_execution_instance',
  PROCESS = 'process',
  PROCESS_END_NODE_DETAILS = 'process_end_node_details',
  PROCESS_FLOWCHART = 'process_flowchart',
  PROCESS_FLOWCHART_CONFIG = 'process_flowchart_config',
  PROCESS_FLOWCHART_CONFIG_VERSION = 'process_flowchart_config_version',
  PROCESS_FLOWCHART_EDGE_CONFIG = 'process_flowchart_edge_config',
  PROCESS_FLOWCHART_NODE_CONFIG = 'process_flowchart_node_config',
  PROCESS_OUTPUT = 'process_output',
  PROCESS_OUTPUT_NODE_DETAILS = 'process_output_node_details',
  PROCESS_OUTPUT_SCHEMA = 'process_output_schema',
  PROCESS_ROOT_NODE_DETAILS = 'process_root_node_details',
  PROCESS_ROUTER_FUNCTION = 'process_router_function',
  PROCESS_ROUTER_NODE_DETAILS = 'process_router_node_details',
  PROCESS_SCHEMA = 'process_schema',
  PROCESS_TASK = 'process_task',
  PROCESS_TASK_DEFINITION = 'process_task_definition',
  PROCESS_TASK_DEFINITION_VERSION = 'process_task_definition_version',
  PROCESS_TASK_NODE_DETAILS = 'process_task_node_details',
  PROCESS_TASK_SCHEMA = 'process_task_schema',
  PROCESS_TERMINAL_NODE_DETAILS = 'process_terminal_node_details',
  PROJECT = 'project',
  PROTEIN = 'protein',
  PROTEIN_ALIGNMENT = 'protein_alignment',
  PROTEIN_FORMAT = 'protein_format',
  PROTEIN_SCHEMA = 'protein_schema',
  PROTOCOL = 'protocol',
  PUBLISHING_RECORD = 'publishing_record',
  REACTION = 'reaction',
  RECIPE_PROCEDURE = 'recipe_procedure',
  RECIPE_PROCESS = 'recipe_process',
  RECIPE_PROCESS_OUTPUT = 'recipe_process_output',
  RECIPE_RUN_TASK = 'recipe_run_task',
  RECIPE_STEP_TASK = 'recipe_step_task',
  RECIPE_UNIT_OPERATION_TASK = 'recipe_unit_operation_task',
  REGISTRATION_TABLE = 'registration_table',
  REGISTRATION_TABLE_CONFIG = 'registration_table_config',
  REGISTRY = 'registry',
  REQUEST = 'assay_request',
  REQUEST_SCHEMA = 'request_schema',
  REQUEST_TASK_SCHEMA = 'request_task_schema',
  REQUEST_V2_DEFINITION = 'request_v2_definition',
  REQUEST_V2_SUBMISSION = 'request_v2_submission',
  RESULT = 'result',
  RESULT_SCHEMA = 'result_schema',
  REVIEW = 'review',
  REVIEW_LIFECYCLE = 'review_lifecycle',
  RNA_OLIGO = 'rna_oligo',
  RNA_OLIGO_LINK_FIELD_DEFINITION = 'rna_oligo_link_field_definition',
  RNA_OLIGO_SCHEMA = 'rna_oligo_schema',
  RNA_SEQUENCE = 'rna_sequence',
  RNA_SEQUENCE_LINK_FIELD_DEFINITION = 'rna_sequence_link_field_definition',
  RNA_SEQUENCE_SCHEMA = 'rna_sequence_schema',
  ROUTINE = 'routine',
  ROUTINE_EXECUTION = 'routine_execution',
  RUN = 'run',
  RUN_SCHEMA = 'run_schema',
  SALT = 'salt',
  SAMPLE = 'sample',
  SAMPLE_DEFINITION = 'sample_definition',
  SAMPLE_TYPE = 'sample_type',
  SAVED_SEARCH = 'saved_search',
  SCHEMA = 'schema',
  SCHEMA_FIELD = 'tag_schema_field',
  SCHEMA_FIELD_SELECTOR = 'schema_field_selector',
  SCHEMA_FIELD_SELECTOR_OPTION = 'schema_field_selector_option',
  SCHEMA_INTERFACE = 'schema_interface',
  SCHEMA_LINK_FIELD_DEFINITION = 'schema_link_field_definition',
  SCRATCH_FILE = 'scratch_file',
  SCRATCH_PLATE = 'scratch_plate',
  SEQUENCE = 'sequence',
  SEQUENCE_ANALYSIS = 'sequence_analysis',
  SEQUENCE_PRIMER = 'sequence_primer',
  SERVICE_ACCOUNT = 'service_account',
  SIMPLE_TABLE_DOC = 'SIMPLE_TABLE_DOC',
  SOLVENT = 'solvent',
  SOURCE = 'source',
  SQL_DASHBOARD = 'sql_dashboard',
  SQL_DASHBOARD_BLOCK = 'sql_dashboard_block',
  SQL_DASHBOARD_LINK = 'sql_dashboard_link',
  STAGE_CONFIGURATION = 'stage_configuration',
  STAGE_ENTRY = 'stage_entry',
  STAR = 'star',
  STATIC_SCHEMA_INTERFACE = 'STATIC_SCHEMA_INTERFACE',
  STATIC_VALIDATION_RULE_TEMPLATE = 'static_validation_rule_template',
  STORABLE = 'STORABLE',
  STORABLE_LINK_FIELD_DEFINITION = 'storable_link_field_definition',
  STRUCTURED_TABLE = 'structured_table',
  STUDY = 'study',
  STUDY_DESIGN = 'study_design',
  STUDY_ITEM_ASSOCIATION = 'study_item_association',
  STUDY_SAMPLE_TEST_PLAN = 'study_sample_test_plan',
  STUDY_SCHEMA = 'study_schema',
  SUBTEMPLATE = 'subtemplate',
  SYSTEM_CATEGORY_LINK_FIELD_DEFINITION = 'system_category_link_field_definition',
  TAG_SCHEMA = 'tag_schema',
  TEAM = 'team',
  TEMPLATE = 'template',
  TEMPLATE_COLLECTION = 'template_collection',
  TENANT_CONFIG = 'tenant_config',
  TENTACLE = 'tentacle_table',
  TEST_DEFINITION = 'test_definition',
  TEST_ORDER = 'test_order',
  TEXT_FIELD_DEFINITION = 'text_field_definition',
  TFI_ACCOUNT = 'tfi_account',
  TRANSCRIPTION_LINK_FIELD_DEFINITION = 'transcription_link_field_definition',
  TRANSFER_TABLE = 'transfer_table',
  TRANSFER_TABLE_CONFIG = 'transfer_table_config',
  TRANSFER_TABLE_PLACEHOLDER = 'transfer_table_placeholder',
  TRANSLATION_LINK_FIELD_DEFINITION = 'translation_link_field_definition',
  TREATMENT_GROUP = 'treatment_group',
  TRIAL_SAMPLE_TESTING_STUDY_DESIGN = 'trial_sample_testing_study_design',
  UNIT = 'unit',
  UNIT_TYPE = 'quantity',
  USER = 'user',
  VALIDATION_RULE = 'validation_rule',
  WAREHOUSE_CREDENTIAL = 'warehouse_credential',
  WAREHOUSE_CUSTOM_VIEW = 'warehouse_custom_view',
  WELL_PLATE = 'well_plate',
  WELL_PLATE_SCHEMA = 'well_plate_schema',
  WORKFLOW = 'workflow',
  WORKFLOW_TEMPLATE = 'workflow_template',
  WORKLIST = 'worklist',
  WORKSHEET = 'worksheet',
}

export type SchemaType = ModelTypeConstants.BATCH_SCHEMA | ModelTypeConstants.TAG_SCHEMA;

export type EntryType =
  | ModelTypeConstants.ENTRY
  | ModelTypeConstants.STAGE_CONFIGURATION
  | ModelTypeConstants.STAGE_ENTRY
  | ModelTypeConstants.SUBTEMPLATE
  | ModelTypeConstants.TEMPLATE
  | ModelTypeConstants.DOCUMENT;

export type ProcessType =
  | ModelTypeConstants.PROCESS
  | ModelTypeConstants.RECIPE_PROCESS
  | ModelTypeConstants.ASSAY_PROCESS;

export const PROCESS_TYPES: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.PROCESS,
  ModelTypeConstants.RECIPE_PROCESS,
  ModelTypeConstants.ASSAY_PROCESS,
] as const;

export type FolderItemType =
  | ProcessType
  | ModelTypeConstants.BASIC_FOLDER_ITEM
  | ModelTypeConstants.BULK_ASSEMBLY
  | ModelTypeConstants.DATASET
  | ModelTypeConstants.ENTRY
  | ModelTypeConstants.FILE
  | ModelTypeConstants.MIXTURE
  | ModelTypeConstants.MOLECULE
  | ModelTypeConstants.PROTEIN
  | ModelTypeConstants.PROTOCOL
  | ModelTypeConstants.SEQUENCE
  | ModelTypeConstants.STUDY;

export const FOLDER_ITEM_TYPES: ReadonlyArray<ModelTypeConstants> = [
  ...PROCESS_TYPES,
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.DATASET,
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.FILE,
  ModelTypeConstants.MIXTURE,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.PROTOCOL,
  ModelTypeConstants.REQUEST_V2_SUBMISSION,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.STUDY,
] as const;

// Keep in-sync with ShareLink._linked_models
// eslint-disable-next-line no-restricted-syntax
export const SHARE_LINK_ITEMS: Array<ModelTypeConstants> = _.without(
  FOLDER_ITEM_TYPES,
  ModelTypeConstants.DATASET,
  ...PROCESS_TYPES,
  ModelTypeConstants.MIXTURE,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.REQUEST_V2_SUBMISSION,
  ModelTypeConstants.FILE
);
SHARE_LINK_ITEMS.push(ModelTypeConstants.CHAT_SESSION);

// Keep in-sync with benchling/exports/human_exports/app/
// eslint-disable-next-line no-restricted-syntax
export const EXPORTABLE_FOLDER_ITEMS: Array<ModelTypeConstants> = _.without(
  FOLDER_ITEM_TYPES,
  ModelTypeConstants.DATASET,
  ...PROCESS_TYPES,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.REQUEST_V2_SUBMISSION
);

// Keep in-sync with FolderItemMixin.clone implementation
// eslint-disable-next-line no-restricted-syntax
export const COPYABLE_FOLDER_ITEMS: Array<ModelTypeConstants> = _.without(
  FOLDER_ITEM_TYPES,
  ModelTypeConstants.DATASET,
  ...PROCESS_TYPES,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.REQUEST_V2_SUBMISSION
);

// eslint-disable-next-line no-restricted-syntax
export const QUICK_EDIT_FOLDER_ITEMS: Array<ModelTypeConstants> = _.without(
  FOLDER_ITEM_TYPES,
  ModelTypeConstants.DATASET,
  ModelTypeConstants.PROTOCOL,
  ...PROCESS_TYPES,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.REQUEST_V2_SUBMISSION
);

// eslint-disable-next-line no-restricted-syntax
export const CUSTOM_TAGGABLE_FOLDER_ITEMS: Array<ModelTypeConstants> = _.without(
  FOLDER_ITEM_TYPES,
  ...PROCESS_TYPES,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.REQUEST_V2_SUBMISSION
);

/** List of items that can show user presence when viewed in a buffer. */
export const PRESENCE_ENABLED_FOLDER_ITEMS: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.ENTRY,
] as const;

/**
 * TypeScript type representing the ModelTypeConstants values relevant in supporting the oligo/long sequence
 * split. Variables typed with SequenceSubtype are expected to account for the DNA/oligo split explicitly as
 * part of the static type checking process by selecting the most appropriate ModelTypeConstants value.
 *
 * SEQUENCE here always means long sequence as opposed to oligos, not the generic term for either.
 */
export type SequenceSubtype =
  | ModelTypeConstants.SEQUENCE
  | ModelTypeConstants.DNA_OLIGO
  | ModelTypeConstants.RNA_OLIGO
  | ModelTypeConstants.RNA_SEQUENCE;

export const SEQUENCE_SUBTYPES: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.DNA_OLIGO,
  ModelTypeConstants.RNA_OLIGO,
  ModelTypeConstants.RNA_SEQUENCE,
] as const;

// The bioentity search endpoint parses "oligo" to mean all oligos, so we use "dna_oligo" instead
// as the filter type for only DNA oligos.
export const DNA_OLIGO_SEARCH_TYPE = 'dna_oligo';

export const RNA_OLIGO_SEARCH_TYPE = 'rna_oligo';

// These special filters allow searching by sequence subtypes.
export const ALL_DNA_SEARCH_TYPE = 'all_dna';
export const ALL_RNA_SEARCH_TYPE = 'all_rna';

type NonSequenceBioentityType =
  | ModelTypeConstants.BASIC_FOLDER_ITEM
  | ModelTypeConstants.PROTEIN
  | ModelTypeConstants.MIXTURE
  | ModelTypeConstants.MOLECULE;

export type BioentityType = SequenceSubtype | NonSequenceBioentityType;

export const LONG_SEQUENCE_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE: Record<NucleotideType, SequenceSubtype> = {
  [NucleotideType.DNA]: ModelTypeConstants.SEQUENCE,
  [NucleotideType.RNA]: ModelTypeConstants.RNA_SEQUENCE,
};

export const OLIGO_NUCLEOTIDE_TYPE_TO_SEQUENCE_SUBTYPE: Record<NucleotideType, SequenceSubtype> = {
  [NucleotideType.DNA]: ModelTypeConstants.DNA_OLIGO,
  [NucleotideType.RNA]: ModelTypeConstants.RNA_OLIGO,
};

export const NOTEBOOK_ITEMS: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.PROTOCOL,
] as const;

export type StorableItemType =
  | ModelTypeConstants.BOX
  | ModelTypeConstants.CONTAINER
  | ModelTypeConstants.LOCATION
  | ModelTypeConstants.PLATE
  | ModelTypeConstants.MATRIX_PLATE
  | ModelTypeConstants.FIXED_PLATE;

export const STORABLE_ITEM_TYPES: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.BOX,
  ModelTypeConstants.CONTAINER,
  ModelTypeConstants.LOCATION,
  ModelTypeConstants.PLATE,
] as const;

export const PARENT_STORABLE_ITEMS: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.BOX,
  ModelTypeConstants.LOCATION,
  ModelTypeConstants.MATRIX_PLATE,
] as const;

export const PREVIEWABLE_NOTE_LINKED_OBJECT_TYPES: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.NOTE_LINKED_FILE,
] as const;

export const POLYMERS: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.DNA_OLIGO,
] as const;

export const STORABLE_TO_SCHEMA: Partial<Record<StorableItemType, ModelTypeConstants>> = {
  [ModelTypeConstants.BOX]: ModelTypeConstants.BOX_SCHEMA,
  [ModelTypeConstants.CONTAINER]: ModelTypeConstants.CONTAINER_SCHEMA,
  [ModelTypeConstants.LOCATION]: ModelTypeConstants.LOCATION_SCHEMA,
  [ModelTypeConstants.PLATE]: ModelTypeConstants.PLATE_SCHEMA,
};

export const SCHEMA_TO_STORABLE: Partial<Record<ModelTypeConstants, StorableItemType>> = {
  [ModelTypeConstants.BOX_SCHEMA]: ModelTypeConstants.BOX,
  [ModelTypeConstants.CONTAINER_SCHEMA]: ModelTypeConstants.CONTAINER,
  [ModelTypeConstants.LOCATION_SCHEMA]: ModelTypeConstants.LOCATION,
  [ModelTypeConstants.PLATE_SCHEMA]: ModelTypeConstants.PLATE,
};

export type SearchableExclusiveSSIType =
  | ModelTypeConstants.ANTIBODY
  | ModelTypeConstants.OLIGO_CONJUGATE
  | ModelTypeConstants.OLIGO_DUPLEX
  | ModelTypeConstants.EQUIPMENT;

export const SEARCHABLE_EXCLUSIVE_SSI_TYPES: Array<SearchableExclusiveSSIType> = [
  ModelTypeConstants.ANTIBODY,
  ModelTypeConstants.OLIGO_CONJUGATE,
  ModelTypeConstants.OLIGO_DUPLEX,
  ModelTypeConstants.EQUIPMENT,
];

export type NoteLinkedDataType = ModelTypeConstants.NOTE_LINKED_PLATE_DESIGN;

export default ModelTypeConstants;
